import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, User, Loader2, Trash2, Sparkles, Phone, Mail } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type Msg = { role: 'user' | 'assistant'; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pos-tutor`;

const QUICK_TOPICS = [
  { label: 'Ouvrir une caisse', prompt: 'Comment ouvrir une session de caisse ?' },
  { label: 'Encaisser un client', prompt: 'Comment encaisser un client ?' },
  { label: 'Paiement fractionné', prompt: 'Comment faire un paiement fractionné ?' },
  {
    label: 'Tablette serveur',
    prompt: 'Comment utiliser la tablette serveur pour prendre les commandes en salle ?',
  },
  { label: 'Cuisine / KDS', prompt: "Comment fonctionne l'écran cuisine (KDS) ?" },
  {
    label: 'Plan de salle',
    prompt: 'Comment configurer les tables et le plan de salle restaurant ?',
  },
];

function parseSuggestions(content: string): { cleanContent: string; suggestions: string[] } {
  const suggestionsMatch = content.match(/<suggestions>\s*(\[.*?\])\s*<\/suggestions>/s);
  let suggestions: string[] = [];
  let cleanContent = content;
  if (suggestionsMatch) {
    try {
      suggestions = JSON.parse(suggestionsMatch[1]);
    } catch {
      /* ignore */
    }
    cleanContent = content.replace(/<suggestions>[\s\S]*?<\/suggestions>/g, '').trim();
  }
  return { cleanContent, suggestions };
}

interface PosyChatPanelProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** If true, renders only the inner content (no Sheet wrapper) */
  contentOnly?: boolean;
}

export default function PosyChatPanel({
  trigger,
  open: controlledOpen,
  onOpenChange,
  contentOnly,
}: PosyChatPanelProps) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;
      const userMsg: Msg = { role: 'user', content: text.trim() };
      const allMessages = [...messages, userMsg];
      setMessages(allMessages);
      setInput('');
      setSuggestions([]);
      setIsLoading(true);

      let assistantSoFar = '';

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          toast.error('Vous devez être connecté.');
          setIsLoading(false);
          return;
        }

        // apikey header is required by the Supabase Edge Functions gateway in addition to the user JWT.
        const resp = await fetch(CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ messages: allMessages }),
        });

        if (resp.status === 429) {
          toast.error('Trop de requêtes.');
          setIsLoading(false);
          return;
        }
        if (!resp.ok || !resp.body) throw new Error('Erreur');

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let textBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          textBuffer += decoder.decode(value, { stream: true });

          let newlineIndex: number;
          while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
            let line = textBuffer.slice(0, newlineIndex);
            textBuffer = textBuffer.slice(newlineIndex + 1);
            if (line.endsWith('\r')) line = line.slice(0, -1);
            if (line.startsWith(':') || line.trim() === '') continue;
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6).trim();
            if (jsonStr === '[DONE]') break;
            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content as string | undefined;
              if (content) {
                assistantSoFar += content;
                const { cleanContent } = parseSuggestions(assistantSoFar);
                setMessages((prev) => {
                  const last = prev[prev.length - 1];
                  if (last?.role === 'assistant') {
                    return prev.map((m, i) =>
                      i === prev.length - 1 ? { ...m, content: cleanContent } : m,
                    );
                  }
                  return [...prev, { role: 'assistant', content: cleanContent }];
                });
              }
            } catch {
              textBuffer = line + '\n' + textBuffer;
              break;
            }
          }
        }

        // Final flush
        if (textBuffer.trim()) {
          for (let raw of textBuffer.split('\n')) {
            if (!raw) continue;
            if (raw.endsWith('\r')) raw = raw.slice(0, -1);
            if (raw.startsWith(':') || raw.trim() === '') continue;
            if (!raw.startsWith('data: ')) continue;
            const jsonStr = raw.slice(6).trim();
            if (jsonStr === '[DONE]') continue;
            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content as string | undefined;
              if (content) assistantSoFar += content;
            } catch {
              /* ignore */
            }
          }
        }

        const { cleanContent, suggestions: finalSuggestions } = parseSuggestions(assistantSoFar);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return prev.map((m, i) =>
              i === prev.length - 1 ? { ...m, content: cleanContent } : m,
            );
          }
          return [...prev, { role: 'assistant', content: cleanContent }];
        });
        if (finalSuggestions.length > 0) setSuggestions(finalSuggestions);
      } catch (e) {
        console.error(e);
        toast.error("Erreur de communication avec l'assistant");
      } finally {
        setIsLoading(false);
        inputRef.current?.focus();
      }
    },
    [messages, isLoading],
  );

  const chatContent = (
    <>
      <SheetHeader className="border-b border-border p-4 pb-2">
        <div className="flex items-center justify-between">
          <SheetTitle className="flex items-center gap-2 text-sm">
            <Bot className="h-4 w-4 text-primary" />
            Posy — Aide VisionPos
          </SheetTitle>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                setMessages([]);
                setSuggestions([]);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </SheetHeader>

      {/* Messages */}
      <ScrollArea className="min-h-0 flex-1" ref={scrollRef as any}>
        <div className="space-y-4 p-4">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="shrink-0 rounded-full bg-primary/10 p-1.5">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="text-sm text-muted-foreground">
                  <p className="mb-1 font-medium text-foreground">Bonjour ! Je suis Posy 👋</p>
                  <p>Posez-moi une question ou choisissez un sujet :</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_TOPICS.map((t) => (
                  <button
                    key={t.label}
                    onClick={() => sendMessage(t.prompt)}
                    className="rounded-full bg-accent px-2.5 py-1.5 text-xs text-accent-foreground transition-colors hover:bg-accent/80"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="space-y-1 border-t border-border pt-2 text-xs text-muted-foreground">
                <p className="flex items-center gap-1.5">
                  <Phone className="h-3 w-3" /> 04 81 06 06 31
                </p>
                <p className="flex items-center gap-1.5">
                  <Mail className="h-3 w-3" /> hello@rylex.fr
                </p>
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div
                key={i}
                className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                {msg.role === 'assistant' && (
                  <div className="mt-0.5 h-fit shrink-0 rounded-full bg-primary/10 p-1">
                    <Bot className="h-3.5 w-3.5 text-primary" />
                  </div>
                )}
                <div
                  className={cn(
                    'max-w-[85%] rounded-xl px-3 py-2 text-sm',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground',
                  )}
                >
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:my-1 [&>ul]:my-1">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="mt-0.5 h-fit shrink-0 rounded-full bg-muted p-1">
                    <User className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))
          )}
          {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
            <div className="flex gap-2">
              <div className="shrink-0 rounded-full bg-primary/10 p-1">
                <Bot className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="rounded-xl bg-muted px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-border px-4 py-2">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => sendMessage(s)}
              disabled={isLoading}
              className="flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs text-accent-foreground transition-colors hover:bg-accent/80 disabled:opacity-50"
            >
              <Sparkles className="h-3 w-3" />
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage(input);
        }}
        className="flex gap-2 border-t border-border p-3"
      >
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Posez votre question..."
          disabled={isLoading}
          className="flex-1 text-sm"
        />
        <Button
          type="submit"
          size="icon"
          disabled={!input.trim() || isLoading}
          className="shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </>
  );

  // Content-only mode: render just the chat content (for external Sheet wrapper)
  if (contentOnly) {
    return <div className="flex h-full flex-col">{chatContent}</div>;
  }

  // Controlled mode (open/onOpenChange provided)
  if (controlledOpen !== undefined) {
    return (
      <Sheet open={controlledOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col p-0 sm:w-[420px]">
          {chatContent}
        </SheetContent>
      </Sheet>
    );
  }

  // Uncontrolled mode with trigger
  return (
    <Sheet>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:w-[420px]">
        {chatContent}
      </SheetContent>
    </Sheet>
  );
}
